import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import './PhoneNumberPopup.css';

// 숫자만 남김 (하이픈·공백 제거)
export const normalizePhone = (v) => (v || '').replace(/\D/g, '');
// 대한민국 휴대폰: 010/011... 로 시작, 총 10~11자리
export const isValidPhone = (v) => /^01\d{8,9}$/.test(normalizePhone(v));
// 입력값을 숫자만 남기고 010-1234-5678 형태로 하이픈 자동 삽입
export const formatPhoneDisplay = (v) => {
  const d = (v || '').replace(/\D/g, '').slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 7) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`;
};

/**
 * 휴대폰 번호 입력·수정 팝업 — 결제(포트원)에 필요. user_profiles.phone_number 에 저장.
 * 기존 닉네임 수정 모달과 동일한 스타일(name-edit-*) 사용.
 * props: onClose(), onSaved(phone)
 */
function PhoneNumberPopup({ onClose, onSaved }) {
  const { t } = useTranslation();
  const { user, profile, refreshProfile } = useAuth();
  const [phone, setPhone] = useState(formatPhoneDisplay(profile?.phone_number || ''));
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (saving) return;
    const normalized = normalizePhone(phone);
    if (!isValidPhone(normalized)) {
      setError(t('phone.invalid'));
      return;
    }
    if (!user?.id) return;
    setSaving(true);
    try {
      const { error: dbErr } = await supabase
        .from('user_profiles')
        .update({ phone_number: normalized, updated_at: new Date() })
        .eq('id', user.id);
      if (dbErr) throw dbErr;
      await refreshProfile?.();
      onSaved?.(normalized);
    } catch (e) {
      console.error('휴대폰 번호 저장 오류:', e?.message || e);
      setError(t('phone.saveError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="name-edit-overlay phone-popup-overlay" onClick={onClose}>
      <div
        className="name-edit-modal name-edit-modal--small"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="name-edit-close"
          onClick={onClose}
          aria-label={t('phone.cancel')}
        >
          ×
        </button>
        <h2 className="name-edit-title">{t('phone.title')}</h2>
        <p className="phone-popup-desc">{t('phone.desc')}</p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSave();
          }}
        >
          <div className="name-edit-form-group">
            <input
              type="tel"
              inputMode="numeric"
              className={`name-edit-input${error ? ' name-edit-input-error' : ''}`}
              value={phone}
              onChange={(e) => {
                setPhone(formatPhoneDisplay(e.target.value));
                if (error) setError('');
              }}
              placeholder={t('phone.placeholder')}
              autoFocus
              maxLength={13}
            />
            {error && <div className="name-edit-error">{error}</div>}
          </div>
          <div className="name-edit-actions">
            <button
              type="button"
              className="name-edit-btn-outline"
              onClick={onClose}
              disabled={saving}
            >
              {t('phone.cancel')}
            </button>
            <button type="submit" className="name-edit-btn-primary" disabled={saving}>
              {saving ? t('phone.saving') : t('phone.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default PhoneNumberPopup;
